import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function BusinessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get(`/businesses/${id}`).then((r) => { setBiz(r.data); setForm(r.data); });
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    try {
      await api.patch(`/businesses/${id}`, {
        business_name: form.business_name, owner_name: form.owner_name,
        phone: form.phone, email: form.email, notes: form.notes,
      });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleStatus = async () => {
    const next = biz.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    await api.patch(`/businesses/${id}`, { status: next });
    toast.success(`Business ${next}`);
    load();
  };

  if (!biz) return <div className="p-8">Loading...</div>;

  return (
    <div data-testid="business-detail-page">
      <button className="flex items-center gap-1 text-sm text-gray-500 mb-4" onClick={() => navigate("/businesses")}>
        <ArrowLeft size={16} /> Back to Businesses
      </button>
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold">{biz.business_name}</h1>
        <StatusBadge status={biz.status} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Edit Business</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {[["business_name", "Business Name"], ["owner_name", "Owner"], ["phone", "Phone"], ["email", "Email"]].map(([k, l]) => (
              <div key={k} className="space-y-1.5">
                <Label>{l}</Label>
                <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-business-${k}`} />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="flex gap-2">
              <Button onClick={save} data-testid="save-business-detail-button">Save</Button>
              <Button variant="outline" onClick={toggleStatus} data-testid="toggle-status-button">
                {biz.status === "ACTIVE" ? "Deactivate" : "Activate"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Assigned Cards ({biz.cards?.length || 0})</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow><TableHead>Card ID</TableHead><TableHead>Location</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {(biz.cards || []).map((c) => (
                  <TableRow key={c.card_code} className="cursor-pointer" onClick={() => navigate(`/cards/${c.card_code}`)}>
                    <TableCell className="font-mono">{c.card_code}</TableCell>
                    <TableCell>{c.location || "—"}</TableCell>
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                  </TableRow>
                ))}
                {(biz.cards || []).length === 0 && (
                  <TableRow><TableCell colSpan={3} className="text-center text-gray-400 py-6">No cards assigned.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
